import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from './ui';
import { clearDeviceToken } from '../utils/deviceIdentity';
import { pushForm } from '../utils/formStack';
import { getOrganisersByUser, GetAllEventsByOrganiserId, GetAllEventsByUserId, AddEvent, GetAllEventTypes, GetAllUsers, ClearUserDevice } from '../api/api';
import usePingLog from '../utils/usePingLog';
import { endSessionLog } from '../utils/session';
import { EVENT_TYPES } from '../utils/helpers';

// Nav items: `bit` → shown when can(bit); `adminOnly` → isAdmin; `superAdminOnly` → isSuperAdmin.
export const NAV = [
  { to: '/app/dashboard', label: 'Dashboard', short: 'Home', icon: 'H', adminOnly: true },
  { to: '/app/events', label: 'Events', short: 'Events', icon: 'E', adminOnly: true },
  { to: '/app/functions', label: 'Functions', short: 'Functions', icon: 'F', adminOnly: true },
  { to: '/app/user-management', label: 'User Management', short: 'Users', icon: 'U', superAdminOnly: true },
  { to: '/app/users', label: 'Members', short: 'Team', icon: 'T', bit: 'CanUsers' },
  { to: '/app/members', label: 'Guests', short: 'Guests', icon: 'G', bit: 'CanMembers' },
  { to: '/app/status', label: 'Guest Status', short: 'Status', icon: 'S', bit: 'CanMembers' },
  { to: '/app/invite-forms', label: 'Invite Forms', short: 'Forms', icon: 'I', bit: 'CanMembers' },
  { to: '/app/reminders', label: 'Reminders', short: 'Remind', icon: '!', bit: 'CanMembers' },
  { to: '/app/cards', label: 'Cards', short: 'Cards', icon: 'C', bit: 'CanMembers' },
  { to: '/app/services', label: 'Services', short: 'Services', icon: '₹', bit: 'CanServices' },
  // Everyone sees My Work: a vendor (caterer, DJ …) has no Can* bits at all.
  { to: '/app/my-work', label: 'My Work', short: 'My Work', icon: 'W', always: true },
  { to: '/app/headcount', label: 'Headcount', short: 'Count', icon: '#', adminOnly: true },
];
// Items on the mobile bottom bar (in NAV order, filtered by visibility).
export const BOTTOM = ['/app/dashboard', '/app/functions', '/app/users', '/app/members', '/app/reminders'];
export const navIcon = (item) => item.icon || item.short?.[0] || item.label?.[0] || '';

const blankEventForm = () => ({
  EventName: '',
  EventTypeId: '',
  EventAdminMode: 'Existing User',
  EventAdminUserId: '',
  EventAdminName: '',
  EventAdminMobileNumber: '',
  EventAdminPassword: '1234',
});
const cleanEventPayload = (values) => Object.fromEntries(
  Object.entries(values).map(([key, value]) => [key, value === '' ? null : value])
);
const userLabel = (row) => [row.Surname, row.Name, row.MobileNumber ? `(${row.MobileNumber})` : ''].filter(Boolean).join(' ');

// All app-shell logic — shared by Layout.desktop / Layout.mobile.
export function useLayout() {
  const {
    user, organiser, event, roleLabel, roleKey, isAdmin, isSuperAdmin, isViewingAs,
    can, selectOrganiser, selectEvent, logout, startViewAs, stopViewAs,
  } = useAuth();
  const nav = useNavigate();
  const toast = useToast();
  usePingLog();

  const visibleNav = NAV.filter(n => (n.always ? true : n.superAdminOnly ? isSuperAdmin : n.adminOnly ? isAdmin : can(n.bit)));
  const bottomNav = visibleNav.filter(n => BOTTOM.includes(n.to));

  const [organisers, setOrganisers] = useState([]);
  const [events, setEvents] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  // The nav drawer is an overlay too: a back press (Android back / Esc /
  // Backspace) must close it before anything navigates. Registered in the same
  // LIFO stack the modals use, so a drawer opened over a form closes first.
  useEffect(() => {
    if (!drawerOpen) return undefined;
    return pushForm({ close: () => setDrawerOpen(false) });
  }, [drawerOpen]);
  const [sidebarClosed, setSidebarClosed] = useState(() => localStorage.getItem('se_sidebar_closed') === '1');
  const [theme, setTheme] = useState(() => localStorage.getItem('se_theme') || 'light');
  const [eventModalOpen, setEventModalOpen] = useState(false);
  const [eventForm, setEventForm] = useState(() => blankEventForm());
  const [savingEvent, setSavingEvent] = useState(false);
  const [eventTypes, setEventTypes] = useState([]);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('se_theme', theme);
  }, [theme]);
  useEffect(() => { localStorage.setItem('se_sidebar_closed', sidebarClosed ? '1' : '0'); }, [sidebarClosed]);
  const toggleTheme = () => setTheme(t => (t === 'dark' ? 'light' : 'dark'));

  const initials = (`${user?.Name || ''} ${user?.Surname || ''}`.trim() || user?.MobileNumber || '?')
    .split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase();
  const fullName = `${user?.Surname || ''} ${user?.Name || ''}`.trim() || 'User';

  // ── Organisers + events for the top-bar picker ─────────────────────────────
  const loadOrganisers = useCallback(() => {
    if (!user) return Promise.resolve([]);
    return getOrganisersByUser({ UserId: user.UserId })
      .then(list => { const arr = Array.isArray(list) ? list : []; setOrganisers(arr); return arr; })
      .catch(() => []);
  }, [user]);
  useEffect(() => { loadOrganisers(); }, [loadOrganisers]);

  // Super Admin sees every organiser's events; everyone else the events they are
  // admin of / on the roster of. Picking an event also selects its organiser.
  // The selected event is read through a ref: it must not be a dependency of
  // loadEvents, or picking an event would trigger another fetch, and so on.
  const eventRef = useRef(event);
  eventRef.current = event;

  const loadEvents = useCallback((sourceOrganisers = organisers) => {
    const orgs = sourceOrganisers.length ? sourceOrganisers : (organiser ? [organiser] : []);
    if (!user) { setEvents([]); selectEvent(null); return Promise.resolve([]); }
    // Super Admin reads events per organiser: until the organiser list has
    // arrived we know nothing, so leave the saved selection alone instead of
    // clearing it (that is what emptied the picker on every page load).
    if (isSuperAdmin && !orgs.length) return Promise.resolve([]);
    const eventRequest = isSuperAdmin
      ? Promise.all(orgs.map(org => (
        GetAllEventsByOrganiserId({ OrganiserId: org.OrganiserId })
          .then(list => (Array.isArray(list) ? list : []).map(ev => ({
            ...ev,
            OrganiserId: ev.OrganiserId || org.OrganiserId,
            OrganiserName: ev.OrganiserName || org.OrganiserName,
          })))
          .catch(() => [])
      ))).then(groups => groups.flat())
      : GetAllEventsByUserId({ UserId: user.UserId }).then(list => (Array.isArray(list) ? list : [])).catch(() => []);

    return eventRequest.then(list => {
      const arr = list.sort((a, b) => String(a.EventName || '').localeCompare(String(b.EventName || '')));
      setEvents(arr);
      const pick = (ev) => {
        const org = orgs.find(o => String(o.OrganiserId) === String(ev.OrganiserId));
        if (org) selectOrganiser(org);
        selectEvent(ev);
      };
      const current = eventRef.current;
      if (!arr.length) {
        if (current) selectEvent(null);
      } else if (!current) {
        pick(arr[0]);
      } else {
        const fresh = arr.find(e => String(e.EventId) === String(current.EventId));
        if (!fresh) pick(arr[0]);
        else if (
          fresh.EventTypeId !== current.EventTypeId || fresh.EventTypeName !== current.EventTypeName ||
          fresh.EventType !== current.EventType || fresh.EventAdminUserId !== current.EventAdminUserId ||
          fresh.OrganiserId !== current.OrganiserId
        ) selectEvent(fresh);
      }
      return arr;
    });
  }, [organiser, organisers, user, isSuperAdmin, selectEvent, selectOrganiser]);

  // Fetch when the signed-in user or the set of organisers changes — not on
  // every render (that used to refetch forever and the picker never settled).
  const eventsKey = `${user?.UserId || ''}|${isSuperAdmin ? 1 : 0}|${organisers.map(o => o.OrganiserId).join(',')}|${organiser?.OrganiserId || ''}`;
  useEffect(() => { loadEvents(); }, [eventsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isSuperAdmin) return;
    GetAllEventTypes().then(rows => setEventTypes(Array.isArray(rows) ? rows : [])).catch(() => setEventTypes([]));
    GetAllUsers().then(rows => setUsers(Array.isArray(rows) ? rows : [])).catch(() => setUsers([]));
  }, [isSuperAdmin]);

  const onEventChange = (id) => {
    const ev = events.find(e => String(e.EventId) === String(id));
    if (!ev) { selectEvent(null); return; }
    const org = organisers.find(o => String(o.OrganiserId) === String(ev.OrganiserId));
    if (org && String(org.OrganiserId) !== String(organiser?.OrganiserId)) selectOrganiser(org);
    selectEvent(ev);
  };

  // ── Super Admin "view as user" ─────────────────────────────────────────────
  const viewAsOptions = useMemo(() => users
    .filter(row => Number(row.Valid) !== 0 && Number(row.IsActive) !== 0)
    .map(row => ({ value: row.UserId, label: userLabel(row) || `User #${row.UserId}` })), [users]);
  const onViewAsChange = async (id) => {
    if (!id) return;
    const target = users.find(row => String(row.UserId) === String(id));
    if (!target) return;
    try {
      const orgs = await getOrganisersByUser({ UserId: target.UserId });
      const list = Array.isArray(orgs) ? orgs : [];
      startViewAs(target, list[0] || null);
      toast(`Viewing as ${target.Name || target.MobileNumber || 'user'}`);
      nav('/app', { replace: true });
    } catch {
      toast('Could not switch user view');
    }
  };
  const returnToSuperAdmin = () => {
    stopViewAs();
    toast('Returned to Super Admin');
    nav('/app/dashboard', { replace: true });
  };

  // ── Super Admin quick "New Event" modal ────────────────────────────────────
  const eventTypeOptions = useMemo(() => (
    eventTypes.length
      ? eventTypes.map(row => ({ value: row.EventTypeId, label: row.EventTypeName }))
      : EVENT_TYPES.map(label => ({ value: label, label }))
  ), [eventTypes]);
  const userOptions = useMemo(() => users.map(row => ({ value: row.UserId, label: userLabel(row) })), [users]);
  const selectedEventType = eventTypes.find(row => String(row.EventTypeId) === String(eventForm.EventTypeId));
  const setEventField = (key, value) => setEventForm(form => ({ ...form, [key]: value }));
  const clearEventForm = () => setEventForm(blankEventForm());
  const eventFormDirty = () => JSON.stringify(eventForm) !== JSON.stringify(blankEventForm());
  const openEventModal = () => {
    if (!organiser && !organisers.length) { toast('No event workspace found for this user'); return; }
    clearEventForm();
    setEventModalOpen(true);
  };
  const closeEventModal = () => { setEventModalOpen(false); clearEventForm(); };

  const addEvent = async () => {
    const targetOrganiser = organiser || organisers[0];
    if (!targetOrganiser || !user) return;
    const payload = cleanEventPayload(eventForm);
    if (!payload.EventName || !payload.EventTypeId) { toast('Event Name and Event Type are required'); return; }
    if (payload.EventAdminMode === 'Existing User' && !payload.EventAdminUserId) { toast('Event Admin is required'); return; }
    if (payload.EventAdminMode === 'New User' && (!payload.EventAdminName || !payload.EventAdminMobileNumber)) {
      toast('New Event Admin name and mobile are required'); return;
    }
    if (selectedEventType) payload.EventType = selectedEventType.EventTypeName;
    else if (payload.EventTypeId && !Number(payload.EventTypeId)) { payload.EventType = payload.EventTypeId; delete payload.EventTypeId; }
    if (payload.EventAdminMode === 'Existing User') {
      delete payload.EventAdminName; delete payload.EventAdminMobileNumber; delete payload.EventAdminPassword;
    } else {
      delete payload.EventAdminUserId;
    }
    delete payload.EventAdminMode;

    setSavingEvent(true);
    try {
      const res = await AddEvent({ ...payload, OrganiserId: targetOrganiser.OrganiserId, Valid: 1, CreatedBy: user.UserId, UpdatedBy: user.UserId });
      const eventId = res?.EventId || res?.insertId;
      const list = await loadEvents();
      const created = list.find(e => String(e.EventId) === String(eventId));
      if (created) selectEvent(created);
      closeEventModal();
      toast('Event created');
      nav('/app/dashboard');
    } catch (err) {
      toast(err?.response?.data?.error || err?.message || 'Failed to add event');
    } finally {
      setSavingEvent(false);
    }
  };

  // ── Session actions ────────────────────────────────────────────────────────
  const doLogout = async () => {
    if (!window.confirm('Sign out on this device?')) return;
    await endSessionLog();
    logout();
    nav('/');
  };
  const forgetDevice = async () => {
    if (!window.confirm('Sign out and unbind this device? The account will re-bind on the next sign-in, and a login from another device will then require OTP.')) return;
    const uid = user?.UserId;
    await endSessionLog();
    try { if (uid) await ClearUserDevice({ UserId: uid }); } catch { /* ignore */ }
    clearDeviceToken(user?.MobileNumber);
    logout();
    nav('/');
  };

  const shellClass = `app-shell role-${roleKey || 'member'}${sidebarClosed ? ' sidebar-closed' : ''}${isViewingAs ? ' viewing-as' : ''}`;

  return {
    user, roleLabel, isSuperAdmin, isViewingAs, initials, fullName, shellClass,
    visibleNav, bottomNav, navIcon,
    events, event, onEventChange,
    drawerOpen, setDrawerOpen, sidebarClosed, setSidebarClosed, theme, toggleTheme,
    viewAsOptions, onViewAsChange, returnToSuperAdmin,
    eventModalOpen, openEventModal, closeEventModal, eventForm, setEventField, clearEventForm, eventFormDirty,
    eventTypeOptions, userOptions, savingEvent, addEvent,
    doLogout, forgetDevice,
  };
}
